/**
 * useVoiceMode — the hands-free Voice Practice loop (design §7.1).
 *
 * Voice mode is a persistent mode (not a per-utterance action): once on, it
 * stays on until the user turns it off. While on, the conversation is spoken
 * end-to-end and the composer text is just a live transcript:
 *
 *   activate → LISTEN → (user pauses) auto-send → coach reply → auto-PLAY →
 *   playback ends → LISTEN → …            (barge-in can cut playback short)
 *
 * The end-of-utterance pause + auto-send live in `useVoiceInput` (silenceMs).
 * This hook owns the rest of the loop: starting/stopping on toggle, auto-playing
 * the coach reply (on activation AND when a new one arrives), resuming listening
 * after playback or an empty session, and enabling voice-activated barge-in.
 *
 * On activation, if the latest message is a coach reply we speak it first, then
 * listen; if it's the user's turn (or nothing to play) we go straight to
 * listening. A reply already heard isn't replayed.
 *
 * Because a spoken turn involves no touching, this hook also owns the loop's
 * relationship with the screen: it holds a wake lock while voice mode is on so
 * the idle timer can't lock the phone mid-sentence, and it suspends the loop
 * cleanly if the app is backgrounded anyway (a call, an app switch, a deliberate
 * power-button press), resuming when the user comes back.
 */
import { useCallback, useEffect, useRef } from "react";
import { AppState } from "react-native";

import { probe } from "@/lib/timing-probe";
import type { PracticeVoice } from "@/types";

import {
  clearCoachAudioCache,
  playCoachMessage,
  stopCoachAudio,
} from "./coach-audio";
import {
  bargeIn,
  isCoachPlaying,
  markPlaybackEnd,
  markPlaybackStart,
  setPlaybackDuration,
} from "./coach-playback";
import { isManualReplayActive, subscribeManualReplay } from "./manual-replay";
import { holdScreenAwake, releaseScreenAwake } from "./screen-wake";
import { subscribeTyping } from "./typing-lock";
import { useVoiceActivatedBargeIn } from "./use-voice-activated-barge-in";
import type { SttSessionEnd } from "./use-voice-input";

/** Errors where resuming the mic would just loop; stop voice mode instead. */
const HARD_ERRORS = new Set([
  "permission_denied",
  "not-allowed",
  "service-not-allowed",
  "audio-capture",
]);

/** Small gap before resuming the mic after an empty/errored session. */
const RELISTEN_DELAY_MS = 500;

/**
 * Backoff for sessions where the mic never opened at all.
 *
 * A session that opens and hears nothing is ordinary — the user was thinking —
 * and retries on the flat delay above forever, which is correct. A session that
 * never reached capture is not ordinary: the engine is failing, and retrying it
 * twice a second produces the getting-ready/ready flicker that looks like a
 * broken mic rather than a broken engine. So those retries slow down, and after
 * `MAX_FAILED_STARTS` voice mode turns itself off and says so — an honest dead
 * end the user can act on beats an animation that never resolves.
 */
const FAILED_START_BACKOFF_MS = [500, 1000, 2000, 4000];
const MAX_FAILED_STARTS = FAILED_START_BACKOFF_MS.length;

/**
 * Cooldown between the coach's clip ending and the mic reopening. The playback
 * promise resolves on the audio's `ended` event, but the speaker tail and the
 * echo canceller's residue outlive it — reopening the mic in the same tick lets
 * that leak in as a phantom "user" turn (the coach answering itself). This gap
 * lets the room go quiet first.
 */
const PLAYBACK_COOLDOWN_MS = 500;

export interface UseVoiceModeOptions {
  /** Whether voice mode is currently on. */
  active: boolean;
  /** Whether an STT path exists on this platform (else the loop is inert). */
  supported: boolean;
  /**
   * Id of the coach reply to speak — i.e. the last message when it's a coach
   * turn, else null (the user's turn / nothing to play). Drives auto-play both
   * on activation and when a new reply arrives.
   */
  coachReplyToPlay: string | null;
  /** Start a listening session (from `useVoiceInput`). */
  startListening: () => void;
  /** Stop listening (from `useVoiceInput`). */
  stopListening: () => void;
  /** Turn voice mode off (used to bail out on a hard error). */
  setActive: (on: boolean) => void;
  /**
   * The loop gave up because the mic would not open. Voice mode has already been
   * switched off; this is the caller's cue to explain why, since the underlying
   * error codes are often ones we deliberately keep silent per-session.
   */
  onUnavailable?: () => void;
  /** Coach audio for this message started playing (reveal the transcript now). */
  onSpeakStart?: (messageId: string) => void;
  /** Coach audio settled — finished OR failed (reveal fallback if not already). */
  onSpeakEnd?: (messageId: string) => void;
  /** Voice choice for coach speech playback. */
  voice: PracticeVoice;
}

export interface VoiceModeApi {
  /**
   * Pass to `useVoiceInput.onSessionEnd`. A captured session becomes a
   * send → reply → replay → listen chain, so here we only handle the empty /
   * errored case: resume listening (transient) or bail out (hard error).
   */
  onSessionEnd: (result: SttSessionEnd) => void;
}

export function useVoiceMode(opts: UseVoiceModeOptions): VoiceModeApi {
  const { active, supported, coachReplyToPlay } = opts;

  // Keep the latest values without re-wiring effects every render.
  const activeRef = useRef(active);
  const startRef = useRef(opts.startListening);
  const stopRef = useRef(opts.stopListening);
  const setActiveRef = useRef(opts.setActive);
  const onUnavailableRef = useRef(opts.onUnavailable);
  const coachReplyRef = useRef(coachReplyToPlay);
  const onSpeakStartRef = useRef(opts.onSpeakStart);
  const onSpeakEndRef = useRef(opts.onSpeakEnd);
  const voiceRef = useRef(opts.voice);
  useEffect(() => {
    activeRef.current = active;
    startRef.current = opts.startListening;
    stopRef.current = opts.stopListening;
    setActiveRef.current = opts.setActive;
    onUnavailableRef.current = opts.onUnavailable;
    coachReplyRef.current = coachReplyToPlay;
    onSpeakStartRef.current = opts.onSpeakStart;
    onSpeakEndRef.current = opts.onSpeakEnd;
    voiceRef.current = opts.voice;
  });

  // Stop any coach audio if the screen unmounts mid-playback, and drop the
  // client audio cache — the per-session replay cache lives only while the user
  // is on this practice screen (background cleanup on leave).
  useEffect(
    () => () => {
      stopCoachAudio();
      clearCoachAudioCache();
    },
    [],
  );

  // Consecutive sessions where the mic never opened. Reset by any session that
  // reached live capture, so a working loop never accumulates a streak.
  const failedStartsRef = useRef(0);
  const playedRef = useRef<string | null>(null);
  const relistenTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cooldownTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearRelisten = useCallback(() => {
    if (relistenTimer.current) {
      clearTimeout(relistenTimer.current);
      relistenTimer.current = null;
    }
    if (cooldownTimer.current) {
      clearTimeout(cooldownTimer.current);
      cooldownTimer.current = null;
    }
  }, []);

  // True while the app is in the background with voice mode still on. Neither
  // platform lets a backgrounded app hold the mic, so the loop parks instead of
  // thrashing a recogniser that can only fail.
  const suspendedRef = useRef(false);

  /**
   * The single gate every "open the mic now" path goes through. Each of them —
   * activation, post-playback cooldown, end-of-typing, retry after an empty
   * session — is scheduled ahead of time, so any of them can land after voice
   * mode was switched off or the app was backgrounded. Checking here means that
   * can't leave a live mic behind whichever path won the race.
   */
  const beginListening = useCallback(() => {
    if (!activeRef.current || suspendedRef.current) return;
    startRef.current();
  }, []);

  // Pause the mic while the user MANUALLY replays a coach clip. Without this the
  // live mic captures the replayed coach voice and auto-sends it. Resume when the
  // replay ends (only if still in voice mode). Auto-play isn't affected — it
  // sequences playback→listen itself; this is scoped to the replay button so it
  // never interferes with barge-in.
  useEffect(() => {
    if (!supported) return;
    return subscribeManualReplay((replaying) => {
      if (replaying) {
        clearRelisten();
        stopRef.current();
      } else {
        beginListening();
      }
    });
  }, [supported, clearRelisten, beginListening]);

  // Pause listening while the user types, and resume when they stop (the typed
  // message was sent, or the composer was cleared — endTyping fires only when the
  // box is empty, so there's never typed text for a resumed mic to overwrite). A
  // brief gap lets the last keystroke's sound fade before the mic reopens. On the
  // send path the auto-play resume also covers this; the isCoachPlaying guard
  // keeps them from fighting.
  useEffect(() => {
    if (!supported) return;
    return subscribeTyping((typing) => {
      if (typing) {
        clearRelisten();
        stopRef.current();
      } else if (activeRef.current && !isCoachPlaying()) {
        clearRelisten();
        relistenTimer.current = setTimeout(() => {
          relistenTimer.current = null;
          if (!isCoachPlaying()) beginListening();
        }, RELISTEN_DELAY_MS);
      }
    });
  }, [supported, clearRelisten, beginListening]);

  // Voice-activated barge-in is DISABLED: its energy VAD can't tell the coach's
  // own voice (heard through the speaker; echo cancellation is imperfect) from the
  // user, so it self-triggered during playback — cutting the coach off and
  // reopening the mic onto the AI's voice. The mic stays cleanly off during
  // playback and resumes when it ends; interrupt by tapping the mic instead.
  useVoiceActivatedBargeIn({
    enabled: false,
    onBargeIn: () => {
      if (isManualReplayActive()) return;
      bargeIn("voice");
      beginListening();
    },
  });

  // Enter / exit voice mode.
  useEffect(() => {
    if (active) {
      // Tapping the mic means "I want to talk" — go straight to listening, and
      // do NOT replay the reply already on screen. It was heard before, or the
      // page was just reloaded (a hard refresh wipes the in-memory "already
      // heard" mark, which otherwise makes activation re-speak the last reply).
      // Seed playedRef so the auto-play effect skips the current reply; only
      // replies that ARRIVE while voice mode is on auto-play.
      playedRef.current = coachReplyRef.current;
      // A deliberate re-activation is a fresh start: whatever went wrong before
      // may well have been fixed (permission granted, headset unplugged), so it
      // should not inherit the streak that turned voice mode off.
      failedStartsRef.current = 0;
      beginListening();
    } else {
      clearRelisten();
      // Turning voice mode off is an explicit fresh start: drop any suspension
      // left over from a background trip so the next activation isn't parked.
      suspendedRef.current = false;
      stopRef.current();
      stopCoachAudio();
    }
    return clearRelisten;
  }, [active, clearRelisten, beginListening]);

  // Hold the screen awake for as long as voice mode is on. A hands-free turn is
  // precisely when nobody is touching the phone, so the idle timer would other-
  // wise lock it mid-sentence — killing the mic session and cutting the coach
  // off until the user woke the screen, which is the whole bug this fixes.
  // Released the moment voice mode ends or the screen unmounts, so the lock can
  // never outlive the conversation that justified it.
  useEffect(() => {
    if (!active || !supported) return;
    holdScreenAwake();
    return releaseScreenAwake;
  }, [active, supported]);

  // Backgrounding is the one interruption a wake lock can't prevent — a call, an
  // app switch, or the user pressing the power button themselves. Neither
  // platform grants the mic to a backgrounded app, so a recogniser left running
  // only fails in a loop. Park the loop instead and pick it up on return: stop
  // the mic and the coach's voice on the way out, resume listening on the way
  // back, with voice mode itself untouched throughout.
  //
  // Only "background" counts. iOS also reports "inactive" for things that never
  // take the app away — the notification shade, Control Centre, the app switcher
  // being peeked at — and tearing the mic down for those would cut off a user
  // who is still mid-sentence.
  useEffect(() => {
    if (!supported) return;
    const sub = AppState.addEventListener("change", (next) => {
      if (next === "background") {
        if (!activeRef.current || suspendedRef.current) return;
        suspendedRef.current = true;
        clearRelisten();
        stopRef.current();
        stopCoachAudio();
        return;
      }
      if (next !== "active" || !suspendedRef.current) return;
      suspendedRef.current = false;
      if (!activeRef.current) return;
      // Re-arm the lock: browsers drop a wake lock whenever the tab is hidden
      // and never restore it, so returning to a still-active voice mode has to
      // ask again. A no-op where the platform kept it.
      holdScreenAwake();
      beginListening();
    });
    return () => sub.remove();
  }, [supported, clearRelisten, beginListening]);

  // Auto-play the coach reply (on activation or when a new one arrives), then
  // resume listening — the core hands-free loop.
  useEffect(() => {
    if (!active || !coachReplyToPlay) return;
    if (playedRef.current === coachReplyToPlay) return;
    playedRef.current = coachReplyToPlay;
    const id = coachReplyToPlay;
    let cancelled = false;
    // MEASUREMENT ONLY — this changes nothing about the loop. How long the
    // coach's voice takes to start is the number the screen's hold is waiting
    // out, and nothing has ever recorded it: `features/voice/metrics.ts`
    // declares `user_stop_to_playback_ms` and no code has ever called
    // `recordLatency`. Until this is read off a device, HELD_REVEAL_CAP_MS is
    // a guess.
    const requestedAt = Date.now();
    void (async () => {
      try {
        await playCoachMessage(id, voiceRef.current, {
          // THE live turn: the one somebody is sitting through, heard once.
          // Streams it rather than waiting for the last byte — worth ~1.2s of
          // the ~2.5s wait, measured on build 17. The replay button leaves this
          // unset and keeps the cache, which is the whole reason it is a flag
          // rather than a change of default.
          live: true,
          onStart: () => {
            probe("voice.audio.play", { ms: Date.now() - requestedAt });
            markPlaybackStart(id);
            onSpeakStartRef.current?.(id); // reveal the transcript with the voice
          },
          onDuration: (ms) => setPlaybackDuration(id, ms),
        });
      } catch {
        // TTS disabled/failed — keep the conversation moving (fall through).
      } finally {
        markPlaybackEnd(id);
        onSpeakEndRef.current?.(id); // reveal fallback if the voice never started
      }
      // Resume listening AFTER a short cooldown so the speaker tail / echo-cancel
      // residue can't leak into the reopened mic as a phantom user turn.
      if (!cancelled && activeRef.current) {
        if (cooldownTimer.current) clearTimeout(cooldownTimer.current);
        cooldownTimer.current = setTimeout(() => {
          cooldownTimer.current = null;
          if (!cancelled) beginListening();
        }, PLAYBACK_COOLDOWN_MS);
      }
    })();
    return () => {
      cancelled = true;
      if (cooldownTimer.current) {
        clearTimeout(cooldownTimer.current);
        cooldownTimer.current = null;
      }
    };
  }, [active, coachReplyToPlay, beginListening]);

  const onSessionEnd = useCallback(
    ({ captured, started, errorCode }: SttSessionEnd) => {
      if (captured) return; // handled by the send → reply → replay → listen path
      if (errorCode && HARD_ERRORS.has(errorCode)) {
        setActiveRef.current(false); // real problem — stop, the notice explains
        return;
      }

      // The mic opened and simply heard nothing: an ordinary silent turn. Clear
      // the failure streak and retry promptly — this is the loop working.
      if (started) {
        failedStartsRef.current = 0;
      } else {
        failedStartsRef.current += 1;
        if (typeof __DEV__ !== "undefined" && __DEV__) {
          // The single most useful breadcrumb when voice "does nothing": it says
          // the mic never opened, how many times running, and the engine's code
          // (which a single session deliberately keeps quiet about).
          console.log(
            "[voice/mode] mic never opened —",
            `attempt ${failedStartsRef.current}/${MAX_FAILED_STARTS}`,
            errorCode ?? "no error reported",
          );
        }
        if (failedStartsRef.current >= MAX_FAILED_STARTS) {
          failedStartsRef.current = 0;
          clearRelisten();
          setActiveRef.current(false);
          onUnavailableRef.current?.();
          return;
        }
      }

      if (!activeRef.current || suspendedRef.current) return;
      const delay = started
        ? RELISTEN_DELAY_MS
        : (FAILED_START_BACKOFF_MS[failedStartsRef.current - 1] ??
          RELISTEN_DELAY_MS);
      clearRelisten();
      relistenTimer.current = setTimeout(() => {
        relistenTimer.current = null;
        beginListening();
      }, delay);
    },
    [clearRelisten, beginListening],
  );

  return { onSessionEnd };
}
